import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    Divider,
    Checkbox,
    FormControlLabel,
    Collapse,
    Select,
    MenuItem,
    FormControl,
    Badge,
    ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const defaultFilters = {
    accountType: [],
    followers: '',
    limit: '',
    contentCount: '',
    viewCount: '',
    locationCountry: '',
};

const FilterSidebar = ({ isOpen, onClose, onApply, initialFilters = {} }) => {
    const [filters, setFilters] = useState(() => ({
        ...defaultFilters,
        ...initialFilters,
    }));

    const [expandedSections, setExpandedSections] = useState({
        accountType: false,
        followers: false,
        limit: false,
        contentCount: false,
        viewCount: false,
        locationCountry: false,
    });

    // Reset all sections to collapsed when drawer opens
    useEffect(() => {
        if (isOpen) {
            setExpandedSections({
                accountType: false,
                followers: false,
                limit: false,
                contentCount: false,
                viewCount: false,
                locationCountry: false,
            });
        }
    }, [isOpen]);

    // Sync filters with initialFilters when sidebar opens or initialFilters change
    useEffect(() => {
        if (isOpen) {
            setFilters({
                ...defaultFilters,
                ...initialFilters,
            });
        }
    }, [isOpen, initialFilters]);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleCheckboxChange = (field, value) => {
        setFilters((prev) => {
            const prevValues = Array.isArray(prev[field]) ? prev[field] : [];
            const isChecked = prevValues.includes(value);
            return {
                ...prev,
                [field]: isChecked
                    ? prevValues.filter((item) => item !== value)
                    : [...prevValues, value],
            };
        });
    };

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section],
        }));
    };

    // Prevent non-numeric characters in number inputs
    const handleKeyDown = (e) => {
        // Block: e, E, +, -, ., and any other non-numeric keys (except backspace, delete, arrow keys, tab)
        const invalidChars = ['e', 'E', '+', '-', '.'];
        if (invalidChars.includes(e.key)) {
            e.preventDefault();
        }
    };

    // Helper function to check if a filter has active values
    const hasActiveFilter = (filterKey) => {
        const value = filters[filterKey];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value !== '' && value !== null && value !== undefined;
    };

    const handleApply = () => {
        const cleanedFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => {
                if (Array.isArray(value)) return value.length > 0;
                return value !== '' && value !== 'all';
            })
        );
        onApply(cleanedFilters);
        onClose();
    };

    const handleReset = () => {
        setFilters({
            accountType: [],
            followers: '',
            limit: '',
            contentCount: '',
            viewCount: '',
            locationCountry: '',
        });
        onApply({});
    };

    const countryOptions = [
        'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia',
        'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
        'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei',
        'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon', 'Canada', 'Central African Republic',
        'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Congo-Brazzaville)', 'Costa Rica', 'Croatia',
        'Cuba', 'Cyprus', 'Czechia', 'Democratic Republic of the Congo', 'Denmark', 'Djibouti', 'Dominica',
        'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
        'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
        'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary',
        'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan',
        'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
        'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi',
        'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico',
        'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
        'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea',
        'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
        'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis',
        'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
        'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
        'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan',
        'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste',
        'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
        'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
        'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
    ];

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        width: { xs: '100%', sm: 450 },
                        p: 0,
                    },
                },
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.75rem' }}>
                        Filters
                    </Typography>
                    <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            <Divider />

            {/* Filters Content */}
            <Box sx={{ px: 3, py: 2, overflowY: 'auto', flexGrow: 1 }}>
                {/* Account Type */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('accountType')}
                    >
                        <Badge
                            variant="dot"
                            invisible={!hasActiveFilter('accountType')}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -4,
                                    top: 4,
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                },
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                Account Type
                            </Typography>
                        </Badge>
                        {expandedSections.accountType ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </Box>
                    <Collapse in={expandedSections.accountType}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 0.5 }}>
                            {['PERSONAL', 'BUSINESS', 'CREATOR'].map((value) => {
                                const checked = Array.isArray(filters.accountType)
                                    ? filters.accountType.includes(value)
                                    : false;

                                return (
                                    <FormControlLabel
                                        key={value}
                                        control={
                                            <Checkbox
                                                checked={checked}
                                                onChange={() => handleCheckboxChange('accountType', value)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <Typography sx={{ fontSize: '0.9rem', textTransform: 'capitalize' }}>
                                                {value.toLowerCase()}
                                            </Typography>
                                        }
                                        sx={{ m: 0 }}
                                    />
                                );
                            })}
                        </Box>
                    </Collapse>
                </Box>

            {/* Creator Country */}
            <Box sx={{ mb: 3 }}>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                    onClick={() => toggleSection('locationCountry')}
                >
                    <Badge
                        variant="dot"
                        invisible={!hasActiveFilter('locationCountry')}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                right: -4,
                                top: 4,
                                width: 6,
                                height: 6,
                                minWidth: 6,
                            },
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                            Creator Country
                        </Typography>
                    </Badge>
                    {expandedSections.locationCountry ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                </Box>
                <Collapse in={expandedSections.locationCountry}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={filters.locationCountry}
                            onChange={(e) => handleFilterChange('locationCountry', e.target.value)}
                            displayEmpty
                            renderValue={(selected) =>
                                !selected ? 'Select a country' : selected
                            }
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                minHeight: 44,
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em>Select a country</em>
                            </MenuItem>
                            {countryOptions.map((country) => (
                                <MenuItem key={country} value={country}>
                                    <ListItemText primary={country} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Collapse>
            </Box>

                {/* Followers */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('followers')}
                    >
                        <Badge
                            variant="dot"
                            invisible={!hasActiveFilter('followers')}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -4,
                                    top: 4,
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                },
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                Followers
                            </Typography>
                        </Badge>
                        {expandedSections.followers ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </Box>
                    <Collapse in={expandedSections.followers}>
                        <TextField
                            fullWidth
                            placeholder="Enter minimum followers"
                            type="number"
                            value={filters.followers}
                            onChange={(e) => handleFilterChange('followers', e.target.value)}
                            onKeyDown={handleKeyDown}
                            size="small"
                            inputProps={{ min: 0 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Collapse>
                </Box>

                {/* Limit of Users */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('limit')}
                    >
                        <Badge
                            variant="dot"
                            invisible={!hasActiveFilter('limit')}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -4,
                                    top: 4,
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                },
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                Limit of Users
                            </Typography>
                        </Badge>
                        {expandedSections.limit ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </Box>
                    <Collapse in={expandedSections.limit}>
                        <TextField
                            fullWidth
                            placeholder="Enter limit (1-500)"
                            type="number"
                            value={filters.limit}
                            onChange={(e) => {
                                const value = e.target.value;
                                // Only allow values between 1 and 500
                                if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= 500)) {
                                    handleFilterChange('limit', value);
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            size="small"
                            inputProps={{ min: 1, max: 500 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Collapse>
                </Box>

                {/* Content Count */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('contentCount')}
                    >
                        <Badge
                            variant="dot"
                            invisible={!hasActiveFilter('contentCount')}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -4,
                                    top: 4,
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                },
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                Creator's Content Count
                            </Typography>
                        </Badge>
                        {expandedSections.contentCount ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </Box>
                    <Collapse in={expandedSections.contentCount}>
                        <TextField
                            fullWidth
                            placeholder="Enter minimum content count"
                            type="number"
                            value={filters.contentCount}
                            onChange={(e) => handleFilterChange('contentCount', e.target.value)}
                            onKeyDown={handleKeyDown}
                            size="small"
                            inputProps={{ min: 0 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Collapse>
                </Box>

                {/* View Count */}
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
                        onClick={() => toggleSection('viewCount')}
                    >
                        <Badge
                            variant="dot"
                            invisible={!hasActiveFilter('viewCount')}
                            color="error"
                            sx={{
                                '& .MuiBadge-badge': {
                                    right: -4,
                                    top: 4,
                                    width: 6,
                                    height: 6,
                                    minWidth: 6,
                                },
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                View Count
                            </Typography>
                        </Badge>
                        {expandedSections.viewCount ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </Box>
                    <Collapse in={expandedSections.viewCount}>
                        <TextField
                            fullWidth
                            placeholder="Enter minimum view count"
                            type="number"
                            value={filters.viewCount}
                            onChange={(e) => handleFilterChange('viewCount', e.target.value)}
                            onKeyDown={handleKeyDown}
                            size="small"
                            inputProps={{ min: 0 }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Collapse>
                </Box>
            </Box>

            {/* Footer Actions */}
            <Box sx={{ p: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleReset}
                        sx={{
                            color: 'grey.600',
                            textTransform: 'none',
                            fontSize: '0.9rem',
                            '&:hover': {
                                bgcolor: 'transparent',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        Clear all
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        sx={{
                            backgroundColor: '#000',
                            textTransform: 'none',
                            px: 4,
                            py: 1.2,
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            '&:hover': {
                                backgroundColor: '#333',
                            },
                        }}
                    >
                        Apply Filter
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default FilterSidebar;

